import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { UseSetLenguajePorPais } from "../../hooks/UseSetLenguajePorPais";
import { extractNumber } from "../../utils/extractNumber";
import SingleObjective from "./SingleObjective";
import { Dispatch, FC, SetStateAction } from "react";
import {
  iObjetivoAprendizaje,
  OAsGroupedByType,
  SelectedIndicator,
} from "../../interfaces/ObjetivosAprendizaje/iObjetivosAprendizaje";
import { useAuthContext } from "../../hooks/Auth";

export interface ObjColors {
  letter: string;
  border: string;
  background: string;
}

const mainObjectivoColors: ObjColors = {
  letter: "#336B87",
  border: "#88A9B9",
  background: "rgba(0, 111, 149, 0.1)",
};

const secondaryObjectivoColors: ObjColors = {
  letter: "#2E7D32",       // Verde oscuro elegante (similar intensidad al azul que usas)
  border: "#81A98A",       // Verde grisáceo suave para bordes
  background: "rgba(46, 125, 50, 0.1)", // Verde con opacidad baja, nada chillón
};

interface Props {
  category: string;
  dataOa: iObjetivoAprendizaje[];
  selectedIndicators: SelectedIndicator;
  expandedPanel: string | null;
  setExpandedPanel: Dispatch<SetStateAction<string | null>>;
  handleIndicatorSelect: (idObjetivo: string, indicador: string) => void;
  handleToggleAllIndicators: (
    idObjetivo: string,
    allIndicators: string[]
  ) => void;
  handleRemoveObjectiveFromProject: (objetivo: iObjetivoAprendizaje) => void;
  handleAddObjectiveToProject: (objetivo: iObjetivoAprendizaje) => void;
  setSelectedIndicators: Dispatch<SetStateAction<SelectedIndicator>>;
}

const SingleType: FC<Props> = ({
  category,
  dataOa,
  selectedIndicators,
  setExpandedPanel,
  expandedPanel,
  handleAddObjectiveToProject,
  handleIndicatorSelect,
  handleRemoveObjectiveFromProject,
  handleToggleAllIndicators,
  setSelectedIndicators,
}) => {
  const { t } = useTranslation();
  UseSetLenguajePorPais();

  const { user } = useAuthContext();
  const IS_FROM_URUGUAY = user?.pais === "UY";

  // Reemplazar implementacion por una mas limpia
  const selectedColors: ObjColors =
    category.toLowerCase() === "competencia específica"
      ? secondaryObjectivoColors
      : mainObjectivoColors;

  // Agrupar objetivos por tipo (para mostrarlos en secciones)
  const groupedObjectives = dataOa.reduce<OAsGroupedByType>(
    (groups, objetivo) => {
      const key = objetivo.tipo;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(objetivo);
      return groups;
    },
    {}
  );

  const objectives = groupedObjectives[category] || [];
  if (objectives.length === 0) return null;

  // Ordena los objetivos de acuerdo al número extraído del código.
  const sortedObjectives = objectives.slice().sort((a, b) => {
    const numA = extractNumber(a.codigo);
    const numB = extractNumber(b.codigo);
    return numA - numB;
  });

  return (
    <Grid item xs={12}>
      {/* Tipo */}
      {!IS_FROM_URUGUAY && (
        <Typography
          sx={{
            fontFamily: "Montserrat",
            color: "#336B87",
            fontSize: "1rem",
          }}
        >
          {t("tipo")}
        </Typography>
      )}

      {/* Nombre */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: 2,
          color: "#336B87",
          textTransform: IS_FROM_URUGUAY ? "uppercase" : "none",
        }}
      >
        {category}
      </Typography>

      {/* Objetivos */}
      <Grid container spacing={3}>
        {sortedObjectives.map((objetivo) => (
          <SingleObjective
            key={objetivo.id_objetivo}
            objetivo={objetivo}
            handleIndicatorSelect={handleIndicatorSelect}
            selectedIndicators={selectedIndicators}
            handleAddObjectiveToProject={handleAddObjectiveToProject}
            handleToggleAllIndicators={handleToggleAllIndicators}
            handleRemoveObjectiveFromProject={handleRemoveObjectiveFromProject}
            expandedPanel={expandedPanel}
            setExpandedPanel={setExpandedPanel}
            setSelectedIndicators={setSelectedIndicators}
            boxColors={selectedColors}
          />
        ))}
      </Grid>
    </Grid>
  );
};

export default SingleType;
